import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, output, signal, untracked } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { NgTemplateOutlet } from '@angular/common'
import { Observable, Subscription } from 'rxjs'
import { TableModule } from 'primeng/table'
import { Select } from 'primeng/select'
import { TooltipModule } from 'primeng/tooltip'
import { FurtherInsured } from '@features/certificate/models/certificate-general-info/certificate-general-info.model'
import { FurtherInsuredLimitsService } from '@features/certificate/services/further-insured-limits/further-insured-limits.service'
import {
  FURTHER_INSURED_LIMIT_ROWS,
  FurtherInsuredLimitRow,
  FurtherInsuredLimitValues,
  unavailableFurtherInsuredLimits
} from '@features/certificate/utils/further-insured-limits.util'
import { PolicyApiResponse } from '@features/insured/models/policy/policy.model'
import { GlobalErrorService } from '@services/global-error.service'
import { hasActualValue } from '@core/utils/form-utils'

export interface FurtherInsuredPolicyOption {
  label: string
  value: string
}

export interface FurtherInsuredLimitsColumn {
  insured: FurtherInsured
  /** Only filled when the insured has several policies in the period, so one has to be chosen. */
  policyOptions: FurtherInsuredPolicyOption[]
  policyId: string | null
  /** `null` while loading, until a policy is chosen or when the limits could not be loaded. */
  values: FurtherInsuredLimitValues | null
}

export interface FurtherInsuredPolicyChange {
  company: string
  policyId: string
}

/** A request that failed; it is not retried while the modal is open. */
const FAILED = 'failed'

/**
 * The limits of the further insured of a certificate: a table with one column per insured on desktop,
 * one card per insured on mobile. Insured with several policies in the period get a select for the policy
 * to show, in which nothing is preselected.
 */
@Component({
  selector: 'albert-further-insured-limits',
  imports: [TableModule, Select, FormsModule, NgTemplateOutlet, TooltipModule],
  templateUrl: './further-insured-limits.component.html',
  styleUrl: './further-insured-limits.component.css',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FurtherInsuredLimitsComponent {
  public readonly insured = input.required<FurtherInsured[]>()
  public readonly periodUuid = input<string | null>(null)
  public readonly editable = input(false)
  public readonly policyChange = output<FurtherInsuredPolicyChange>()
  public readonly rows: FurtherInsuredLimitRow[] = [...FURTHER_INSURED_LIMIT_ROWS]
  // Cached by member and by policy, so choosing a policy (which changes the selection) loads nothing twice.
  private readonly policiesByMember = signal<ReadonlyMap<string, PolicyApiResponse[] | typeof FAILED>>(new Map())
  private readonly limitsByPolicy = signal<ReadonlyMap<string, FurtherInsuredLimitValues | typeof FAILED>>(new Map())
  private readonly pending = signal<ReadonlySet<string>>(new Set())
  public readonly columns = computed<FurtherInsuredLimitsColumn[]>(() => this.insured().map(insured => this.toColumn(insured)))
  public readonly loading = computed(() => this.pending().size > 0)
  private readonly limitsService = inject(FurtherInsuredLimitsService)
  private readonly globalErrorService = inject(GlobalErrorService)
  private readonly requests = new Map<string, Subscription>()

  constructor() {
    effect(() => {
      this.periodUuid()
      // Policies belong to a period, so a new period starts over.
      untracked(() => this.reset())
    })
    effect(() => {
      const periodUuid = this.periodUuid()
      if (!hasActualValue(periodUuid)) {
        return
      }
      const loaded = this.policiesByMember()
      this.insured()
        .filter(insured => !loaded.has(insured.memberId))
        .forEach(insured => this.request(
          `member:${insured.memberId}`,
          this.limitsService.loadPolicies(insured.memberId, periodUuid),
          policies => this.policiesByMember.update(map => new Map(map).set(insured.memberId, policies)),
          () => this.policiesByMember.update(map => new Map(map).set(insured.memberId, FAILED)),
          `The policies of ${insured.company} could not be loaded.`
        ))
    })
    effect(() => {
      const loaded = this.limitsByPolicy()
      this.columns()
        .filter(column => column.policyId !== null && !loaded.has(column.policyId))
        .forEach(column => this.request(
          `policy:${column.policyId}`,
          this.limitsService.loadLimits(column.policyId!),
          values => this.limitsByPolicy.update(map => new Map(map).set(column.policyId!, values)),
          () => this.limitsByPolicy.update(map => new Map(map).set(column.policyId!, FAILED)),
          `The limits of ${column.insured.company} could not be loaded.`
        ))
    })
    inject(DestroyRef).onDestroy(() => this.requests.forEach(subscription => subscription.unsubscribe()))
  }

  /** Empty while the limits are loading, until a policy is chosen or when they could not be loaded. */
  public limitValue(column: FurtherInsuredLimitsColumn, row: FurtherInsuredLimitRow): string {
    return column.values?.[row.key] ?? ''
  }

  public onPolicyChange(column: FurtherInsuredLimitsColumn, policyId: string | null): void {
    if (policyId !== null && policyId !== column.policyId) {
      this.policyChange.emit({ company: column.insured.company, policyId })
    }
  }

  private toColumn(insured: FurtherInsured): FurtherInsuredLimitsColumn {
    const unavailable: FurtherInsuredLimitsColumn = {
      insured,
      policyOptions: [],
      policyId: null,
      values: unavailableFurtherInsuredLimits()
    }
    if (!hasActualValue(this.periodUuid())) {
      return unavailable
    }
    const policies = this.policiesByMember().get(insured.memberId)
    if (policies === undefined || policies === FAILED) {
      return { ...unavailable, values: null }
    }
    if (policies.length === 0) {
      return unavailable
    }
    const policyOptions = policies.length > 1 ? policies.map(policy => ({ label: policy.label, value: policy.uuid! })) : []
    // A saved choice only counts while the policy still belongs to the insured in this period.
    const policyId = policies.length === 1
      ? policies[0]!.uuid!
      : policies.find(policy => policy.uuid === insured.policyId)?.uuid ?? null
    const values = policyId === null ? undefined : this.limitsByPolicy().get(policyId)
    return { insured, policyOptions, policyId, values: values === undefined || values === FAILED ? null : values }
  }

  private request<T>(key: string, source: Observable<T>, next: (value: T) => void, failed: () => void, message: string): void {
    if (this.requests.has(key) || untracked(this.pending).has(key)) {
      return
    }
    const done = (): void => {
      this.requests.delete(key)
      this.pending.update(keys => new Set([...keys].filter(k => k !== key)))
    }
    this.pending.update(keys => new Set(keys).add(key))
    const subscription = source.subscribe({
      next: value => {
        done()
        next(value)
      },
      error: () => {
        done()
        failed()
        this.globalErrorService.reportError({ subsystemName: 'Limits of Further Insured', description: message })
      }
    })
    if (!subscription.closed) {
      this.requests.set(key, subscription)
    }
  }

  private reset(): void {
    this.requests.forEach(subscription => subscription.unsubscribe())
    this.requests.clear()
    this.pending.set(new Set())
    this.policiesByMember.set(new Map())
    this.limitsByPolicy.set(new Map())
  }
}
